import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  User, 
  Search, 
  Filter, 
  Eye,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Award,
  BookOpen,
  Clock,
  Star,
  TrendingUp,
  Users,
  Camera,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const UserProfiles = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterPlan, setFilterPlan] = useState('')
  const [filterActivity, setFilterActivity] = useState('')
  const { getUserProfiles, updateUserStatus } = useFirebase()

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const data = await getUserProfiles()
      setUsers(data || [])
    } catch (error) {
      console.error('Error loading user profiles:', error)
      toast.error('Failed to load user profiles')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (userId, newStatus) => {
    try {
      await updateUserStatus(userId, newStatus)
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, status: newStatus }
          : user
      ))
      toast.success(`User status updated to ${newStatus}`)
    } catch (error) {
      console.error('Error updating user status:', error)
      toast.error('Failed to update user status')
    }
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.phone?.includes(searchTerm)
    
    const matchesStatus = !filterStatus || user.status === filterStatus
    const matchesPlan = !filterPlan || user.subscriptionPlan === filterPlan
    
    let matchesActivity = true
    if (filterActivity) {
      const now = new Date()
      const lastActive = new Date(user.lastActive || user.createdAt)
      
      switch (filterActivity) {
        case 'today':
          matchesActivity = lastActive.toDateString() === now.toDateString()
          break
        case 'week':
          matchesActivity = (now - lastActive) <= 7 * 24 * 60 * 60 * 1000
          break
        case 'month':
          matchesActivity = (now - lastActive) <= 30 * 24 * 60 * 60 * 1000
          break
        case 'inactive':
          matchesActivity = (now - lastActive) > 30 * 24 * 60 * 60 * 1000
          break
      }
    }
    
    return matchesSearch && matchesStatus && matchesPlan && matchesActivity
  })

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'suspended':
        return <XCircle className="h-4 w-4 text-red-600" />
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />
      default:
        return <User className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'suspended':
        return 'bg-red-100 text-red-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'premium':
        return 'bg-purple-100 text-purple-800'
      case 'pro':
        return 'bg-blue-100 text-blue-800'
      case 'basic':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getActivityStatus = (lastActive) => {
    if (!lastActive) return 'Never'
    
    const now = new Date()
    const lastActiveDate = new Date(lastActive)
    const diffHours = (now - lastActiveDate) / (1000 * 60 * 60)
    
    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`
    if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`
    return lastActiveDate.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  const totalUsers = users.length
  const activeUsers = users.filter(u => u.status === 'active').length
  const premiumUsers = users.filter(u => u.subscriptionPlan === 'premium').length
  const recentUsers = users.filter(u => {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    return new Date(u.createdAt) > weekAgo
  }).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <User className="h-8 w-8 mr-3" />
              User Profiles Management
            </h1>
            <p className="text-indigo-100">
              Manage user profiles, personalization settings, and account details
            </p>
          </div>
          <div className="flex space-x-3">
            <Link to="/user-profiles/analytics" className="btn-white">
              <TrendingUp className="h-5 w-5 mr-2" />
              Analytics
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Users className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-green-600">{activeUsers}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Premium Users</p>
              <p className="text-2xl font-bold text-purple-600">{premiumUsers}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Star className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">New This Week</p>
              <p className="text-2xl font-bold text-blue-600">{recentUsers}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                className="input pl-10 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <select
              className="input w-full"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <select
              className="input w-full"
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
            >
              <option value="">All Plans</option>
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
            </select>
          </div>
          <div>
            <select
              className="input w-full"
              value={filterActivity}
              onChange={(e) => setFilterActivity(e.target.value)}
            >
              <option value="">All Activity</option>
              <option value="today">Active Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            User Profiles ({filteredUsers.length})
          </h3>
        </div>
        <div className="card-body p-0">
          {filteredUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>User Details</th>
                    <th>Contact Info</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th>Activity</th>
                    <th>Performance</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td>
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 relative">
                            {user.profileImage ? (
                              <img 
                                src={user.profileImage} 
                                alt={user.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <User className="h-6 w-6 text-gray-600" />
                            )}
                            {user.hasProfileImage && (
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                <Camera className="h-2 w-2 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-gray-900 truncate">
                              {user.name || 'Anonymous User'}
                            </h4>
                            <p className="text-sm text-gray-600 truncate">
                              ID: {user.id.substring(0, 8)}...
                            </p>
                            {user.location && (
                              <div className="flex items-center text-xs text-gray-500 mt-1">
                                <MapPin className="h-3 w-3 mr-1" />
                                {user.location}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="space-y-1">
                          {user.email && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="h-3 w-3 mr-2" />
                              <span className="truncate max-w-xs">{user.email}</span>
                            </div>
                          )}
                          {user.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="h-3 w-3 mr-2" />
                              {user.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="space-y-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                            {getStatusIcon(user.status)}
                            <span className="ml-1">{user.status || 'Active'}</span>
                          </span>
                          {user.isVerified && (
                            <div className="flex items-center text-xs text-green-600">
                              <Shield className="h-3 w-3 mr-1" />
                              Verified
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(user.subscriptionPlan)}`}>
                          {user.subscriptionPlan === 'premium' && <Star className="h-3 w-3 mr-1" />}
                          {user.subscriptionPlan || 'Free'}
                        </span>
                      </td>
                      <td>
                        <div className="space-y-1">
                          <div className="text-sm text-gray-900">
                            {getActivityStatus(user.lastActive)}
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="h-3 w-3 mr-1" />
                            {user.streak || 0} day streak
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="space-y-1">
                          <div className="flex items-center text-sm">
                            <Award className="h-3 w-3 mr-1 text-yellow-500" />
                            <span className="font-medium">{user.totalScore || 0}</span>
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <BookOpen className="h-3 w-3 mr-1" />
                            {user.testsTaken || 0} tests
                          </div>
                          <div className="text-xs text-gray-500">
                            Avg: {user.averageScore || 0}%
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-1" />
                          {user.createdAt 
                            ? new Date(user.createdAt).toLocaleDateString()
                            : 'N/A'
                          }
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/users/view/${user.id}`}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Profile"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <div className="relative group">
                            <button className="text-gray-600 hover:text-gray-900">
                              <Filter className="h-4 w-4" />
                            </button>
                            <div className="absolute right-0 top-6 w-32 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                              <div className="py-1">
                                <button
                                  onClick={() => handleStatusUpdate(user.id, 'active')}
                                  className="block w-full text-left px-3 py-1 text-xs text-green-600 hover:bg-green-50"
                                >
                                  Activate
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(user.id, 'suspended')}
                                  className="block w-full text-left px-3 py-1 text-xs text-red-600 hover:bg-red-50"
                                >
                                  Suspend
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
              <p className="text-gray-600">
                {searchTerm || filterStatus || filterPlan || filterActivity
                  ? 'Try adjusting your search or filter criteria'
                  : 'No users have registered yet'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card hover:shadow-lg transition-shadow duration-200">
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">User Analytics</h3>
            <p className="text-sm text-gray-600 mb-4">
              View detailed user behavior and engagement analytics
            </p>
            <Link to="/user-profiles/analytics" className="btn-primary">
              View Analytics
            </Link>
          </div>
        </div>

        <div className="card hover:shadow-lg transition-shadow duration-200">
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Active Users</h3>
            <p className="text-sm text-gray-600 mb-4">
              View and manage currently active users
            </p>
            <Link to="/user-profiles?status=active" className="btn-success">
              View Active Users
            </Link>
          </div>
        </div>

        <div className="card hover:shadow-lg transition-shadow duration-200">
          <div className="card-body text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Star className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Premium Users</h3>
            <p className="text-sm text-gray-600 mb-4">
              Manage premium subscribers and their benefits
            </p>
            <Link to="/user-profiles?plan=premium" className="btn-warning">
              View Premium Users
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserProfiles
